import type {
  BonusValueEnum,
  Money,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

export enum BonusCodeCondition {
  CustomerNew = 'validation.bonusconditions.customerstatus.new',
  Lotteries = 'validation.bonusconditions.lotteries',
  MaxPlays = 'validation.bonusconditions.maxplays',
  MinPlays = 'validation.bonusconditions.minplays',
  MinTotalCharge = 'validation.bonusconditions.mintotalcharge',
  NoPlays = 'validation.bonusconditions.noplays',
  Subscription = 'validation.bonusconditions.subscription',
}

export const SOFT_CONDITIONS: BonusCodeCondition[] = [
  BonusCodeCondition.MaxPlays,
  BonusCodeCondition.MinPlays,
  BonusCodeCondition.MinTotalCharge,
  BonusCodeCondition.NoPlays,
]

export type BonusCodeCoupon = {
  fulfillable: boolean
  fulfilled: boolean
  info: {
    code: string
    description: string
    endDate?: string
    title: string
    value: {
      amount: number
      type: BonusValueEnum
    }
  }
  minTotalCharge: Maybe<Money>
  productId: string
  unfulfilledConditions: string[]
}

export type BonusCodeConditionPrice = {
  chargeTotal: Money
  feeTotal: Money
}

export enum BonusCodeVerdict {
  Fulfillable = 'fulfillable',
  Fulfilled = 'fulfilled',
  Unfulfillable = 'unfulfillable',
}

export type BonusCodeConditionStatus = {
  hasBlockingConditions: boolean
  isUnfulfilled: boolean
  minTotalChargeFulfilled: boolean
  missingAmount: number
  verdict: Maybe<BonusCodeVerdict>
}

export type CheckoutBonus = {
  code: string
  expiresAt: string
}

export enum BonusCodeSyncActionType {
  None = 'none',
  Remove = 'remove',
  RemoveBySystem = 'removeBySystem',
  RemoveExpired = 'removeExpired',
  ScheduleExpiry = 'scheduleExpiry',
}

export type BonusCodeSyncAction =
  | { code: string; delayMs: number; type: BonusCodeSyncActionType.ScheduleExpiry }
  | { code: string; type: BonusCodeSyncActionType.Remove }
  | { code: string; type: BonusCodeSyncActionType.RemoveExpired }
  | { type: BonusCodeSyncActionType.None }
  | { type: BonusCodeSyncActionType.RemoveBySystem }

// longer expiries are not scheduled, the code gets rechecked on the next visit
export const MAX_EXPIRY_TIMEOUT_MS = 24 * 60 * 60 * 1000

/** Delay until `expiresAt`, clamped to 0 when already expired. `null` if unparseable or beyond `maxMs`. */
export const getExpiryDelayMs = (expiresAt: string, now: number, maxMs = MAX_EXPIRY_TIMEOUT_MS) => {
  const delayMs = new Date(expiresAt).getTime() - now
  if (!Number.isFinite(delayMs) || delayMs > maxMs) return null

  return Math.max(delayMs, 0)
}

export const getBonusCodeVerdict = (
  coupon: Pick<BonusCodeCoupon, 'fulfillable' | 'fulfilled'>,
): BonusCodeVerdict => {
  if (!coupon.fulfillable) return BonusCodeVerdict.Unfulfillable
  return coupon.fulfilled ? BonusCodeVerdict.Fulfilled : BonusCodeVerdict.Fulfillable
}

export const evaluateBonusCode = (
  coupon: Maybe<BonusCodeCoupon> | undefined,
  price: Maybe<BonusCodeConditionPrice> | undefined,
): BonusCodeConditionStatus => {
  if (!coupon) {
    return {
      hasBlockingConditions: false,
      isUnfulfilled: false,
      minTotalChargeFulfilled: true,
      missingAmount: 0,
      verdict: null,
    }
  }

  const { minTotalCharge } = coupon
  const missingAmount =
    minTotalCharge && price
      ? minTotalCharge.value - price.chargeTotal.value - price.feeTotal.value
      : 0
  const minTotalChargeFulfilled = !minTotalCharge || !price || missingAmount <= 0

  const hasBlockingConditions = coupon.unfulfilledConditions.some(
    (condition) => !SOFT_CONDITIONS.includes(condition as BonusCodeCondition),
  )

  return {
    hasBlockingConditions,
    isUnfulfilled: !minTotalChargeFulfilled || hasBlockingConditions,
    minTotalChargeFulfilled,
    missingAmount,
    verdict: getBonusCodeVerdict(coupon),
  }
}

export const resolveCheckoutBonusSync = ({
  checkoutBonus,
  coupon,
  maxTimeoutMs,
  now,
}: {
  checkoutBonus: Maybe<CheckoutBonus> | undefined
  coupon: Maybe<Pick<BonusCodeCoupon, 'fulfillable' | 'fulfilled'>>
  maxTimeoutMs: number
  now: number
}): BonusCodeSyncAction => {
  if (!checkoutBonus) {
    if (!coupon) {
      return { type: BonusCodeSyncActionType.None }
    }

    const { fulfillable, fulfilled } = coupon
    return !fulfillable || fulfilled
      ? { type: BonusCodeSyncActionType.RemoveBySystem }
      : { type: BonusCodeSyncActionType.None }
  }

  if (!coupon) return { code: checkoutBonus.code, type: BonusCodeSyncActionType.Remove }

  const delayMs = getExpiryDelayMs(checkoutBonus.expiresAt, now, maxTimeoutMs)

  if (delayMs === null) return { type: BonusCodeSyncActionType.None }
  if (delayMs === 0)
    return { code: checkoutBonus.code, type: BonusCodeSyncActionType.RemoveExpired }

  return { code: checkoutBonus.code, delayMs, type: BonusCodeSyncActionType.ScheduleExpiry }
}
